/**
 * 67.75: the motor pool drawn — the plan (core/motorPool.ts MOTOR_POOL: the walls, the frame slots, the lanes, the stacker
 * arm's station, the corner robots' bays, the unpack pad) and what stands in it (fillMotorPool: a frame for every stocked
 * vehicle, the supplies' containers), from the kit (rendering/vehicleFrame.ts, motorPool.ts, the base kit's walls).
 * Static: nothing moves and nothing unpacks yet (the owner: "not really impacting play much yet").
 *
 * The look is built in the kit's units (a person 1.8) and scaled with its group, as the modular buildings are:
 *   - the walls: the base kit's instanced modules (moduleLook.ts), a draw per kind;
 *   - the markings: one merged mesh (slot outlines, the lanes' edges, the pad);
 *   - the frames: one instanced mesh per variant (open, and the containers'), the vehicles in the open ones one instanced
 *     mesh per kind (each model folded into one vertex-coloured geometry);
 *   - the stacker arm parked at its station and a set of corner robots in each bay: a draw each.
 * About 18 draws a side whatever the stock (matrices frozen). The vehicles in the frames are the bulk of its triangles (2–7k
 * each, full detail: their silhouettes are boxes, too crude seen through a frame's bars), so past MOTOR_POOL_VEHICLES_PEOPLE
 * from the camera they are hidden and the frames carry the look — one distance a side a frame. In play (`MotorPools`), behind the Lab's
 * *Motor pool* presentation row (?motorpool=1): each side's pool at its site (core/motorPool.ts motorPoolSite) on a level
 * yard (the plinth the modular buildings stand on), built once at the first frame. Draws only: no state, no sim.
 */
import * as THREE from 'three';
import { buildTank } from './tankLooks';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { GameState, MotorPoolLoad, MotorPoolPlan, MotorPoolStack, VehicleKind } from '../types';
import { TEAMS } from '../constants';
import { MOTOR_POOL, motorPoolLoads, fillMotorPool, motorPoolSite, planCentre } from '../core/motorPool';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { buildLayoutLook } from './moduleLook';
import { buildVehicleFrame, FRAME_DECK, type FrameVariant } from './vehicleFrame';
import { buildHandler } from './motorPool';
import { buildVehicleLook, buildVehicleShape } from './vehicleLooks';
import type { VehicleModel } from './vehicleModels';
import { FRAME, hullName, type Livery } from './orbitShips';
import { unitPaletteFor, type UnitPalette } from './unitPalette';
import { seatOnPlinth, seatAt } from './groundSeat';
import { structureTeam } from '../core/body';

/** The kit's person (metres): the look is built at it and scaled with its group. */
const U = 1.8;
/** Past this many people from the camera a pool's vehicles are hidden (the frames, walls and handlers stay). */
export const MOTOR_POOL_VEHICLES_PEOPLE = 150;

let shownOverride: boolean | null = null;
/** The Lab's *Motor pool* row (null: the page's ?motorpool=1). */
export function setMotorPoolShown(on: boolean | null): void { shownOverride = on; }
/** 67.75: whether each side's motor pool is drawn in play — off by default. */
export function motorPoolShown(): boolean {
  if (shownOverride !== null) return shownOverride;
  try { return typeof location !== 'undefined' && new URLSearchParams(location.search).get('motorpool') === '1'; } catch { return false; }
}

/** Fold a model into one vertex-coloured geometry in its root's frame (instanced parts expanded, hidden ones left out). */
export function folded(root: THREE.Object3D): THREE.BufferGeometry {
  root.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const pos: number[] = [], nor: number[] = [], col: number[] = [];
  const m = new THREE.Matrix4(), im = new THREE.Matrix4(), nm = new THREE.Matrix3(), v = new THREE.Vector3(), n = new THREE.Vector3(), c = new THREE.Color();
  root.traverseVisible(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry?.attributes.position) return;
    const geo = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry;
    const P = geo.attributes.position, N = geo.attributes.normal, C = geo.attributes.color;
    const mat = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.MeshLambertMaterial;
    const base = mat?.color ?? new THREE.Color(0xffffff);
    const inst = (mesh as THREE.InstancedMesh).isInstancedMesh ? (mesh as THREE.InstancedMesh) : null;
    for (let k = 0; k < (inst ? inst.count : 1); k++) {
      m.multiplyMatrices(inv, mesh.matrixWorld);
      if (inst) { inst.getMatrixAt(k, im); m.multiply(im); }
      nm.getNormalMatrix(m);
      for (let i = 0; i < P.count; i++) {
        v.fromBufferAttribute(P, i).applyMatrix4(m); pos.push(v.x, v.y, v.z);
        if (N) { n.fromBufferAttribute(N, i).applyMatrix3(nm).normalize(); nor.push(n.x, n.y, n.z); } else nor.push(0, 1, 0);
        if (C) { c.fromBufferAttribute(C, i); if (!mat?.vertexColors) c.multiply(base); } else c.copy(base);
        col.push(c.r, c.g, c.b);
      }
    }
    if (geo !== mesh.geometry) geo.dispose();
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  return g;
}

/** A flat painted strip (kit units) for the markings: w across x, d across z, its centre at (x, z), a hair over the yard. */
function strip(w: number, d: number, x: number, z: number, color: THREE.ColorRepresentation): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, 0.03, d).toNonIndexed().translate(x, 0.02, z), c = new THREE.Color(color), n = g.attributes.position.count;
  g.setAttribute('color', new THREE.Float32BufferAttribute(Array.from({ length: n * 3 }, (_, k) => [c.r, c.g, c.b][k % 3]), 3));
  return g;
}
/** A rectangle's outline as four strips. */
function outline(out: THREE.BufferGeometry[], w: number, d: number, x: number, z: number, color: THREE.ColorRepresentation, t = 0.18): void {
  out.push(strip(w, t, x, z - d / 2, color), strip(w, t, x, z + d / 2, color), strip(t, d, x - w / 2, z, color), strip(t, d, x + w / 2, z, color));
}

/** The container a load stands in: an open frame for a vehicle; for supplies, its packing's container (the stores and the kits SUPPLY). */
const variantOf = (l: MotorPoolLoad): FrameVariant => 'vehicle' in l ? 'open' : l.container === 'stores' || l.container === 'kits' ? 'supply' : l.container;
/** A vehicle as play draws it (game/actionHost.ts: the 6×6 as the BTR, the tracked carrier as the M113, the rest their D looks). */
export const vehicleModel = (k: VehicleKind, p: UnitPalette): VehicleModel => k === 'wheeled' ? buildVehicleShape('btr', p, U) : k === 'tracked' ? buildVehicleShape('m113', p, U) : k === 'tank' ? buildTank(p, U).m : buildVehicleLook(k, p, U);   // (69.29: the tank the tracked D look)
const lambert = () => new THREE.MeshLambertMaterial({ vertexColors: true });

export interface MotorPoolLookCost { draws: number; triangles: number; frames: number }

/** A stack's place in the plan's frame (the kit's units): its slot's middle, its tier's height, turned along x. */
function stackPose(plan: MotorPoolPlan, s: Pick<MotorPoolStack, 'slot' | 'tier'>): THREE.Matrix4 {
  const sl = plan.slots[s.slot], c = planCentre(plan, sl.i, sl.j, 3, 1);
  return new THREE.Matrix4().compose(new THREE.Vector3(c.x * U, s.tier * FRAME.height + 0.02, c.z * U), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2), new THREE.Vector3(1, 1, 1));
}
/** Where a stack's frame stands in the plan's frame (the kit's units): its floor's middle, and its turn (along x). */
export function stackPoint(plan: MotorPoolPlan, s: Pick<MotorPoolStack, 'slot' | 'tier'>): THREE.Vector3 {
  return new THREE.Vector3().setFromMatrixPosition(stackPose(plan, s));
}

/**
 * A motor pool's look in the kit's units (a person 1.8; scale the group by the match's person ÷ 1.8): the origin at the
 * compound's ground centre, x along the plan's columns, z along its rows (the gate at −z). Its stacks can change in play
 * (`place`: 69.10's frames stacked as they come): the frames are one instanced mesh per variant and the vehicles one per kind,
 * each sized to the plan's every spot and made the first time a variant or kind stands in it.
 */
export class MotorPoolLook {
  readonly group = new THREE.Group();
  readonly vehicles = new THREE.Group();
  /** The stacker arm parked at its station (hidden while a lighter's frames are being stacked: 69.10 draws it at work). */
  readonly arm: THREE.Mesh;
  readonly cost: MotorPoolLookCost = { draws: 0, triangles: 0, frames: 0 };
  private readonly frames = new Map<FrameVariant, { im: THREE.InstancedMesh; tris: number }>();
  private readonly cars = new Map<VehicleKind, { im: THREE.InstancedMesh; tris: number }>();
  private readonly own: THREE.Mesh[] = [];
  private readonly walls: ReturnType<typeof buildLayoutLook> | null;
  private readonly capacity: number;
  private fixed = { draws: 0, triangles: 0 };
  private no = 1;

  constructor(private readonly plan: MotorPoolPlan, private readonly p: UnitPalette, private readonly livery: Livery, opts: { walls?: boolean } = {}) {
    this.group.name = `motorPool:${plan.key}`;
    this.capacity = plan.slots.reduce((n, sl) => n + sl.tiers, 0);
    const at = (i: number, j: number, cols = 1, rows = 1) => { const c = planCentre(plan, i, j, cols, rows); return { x: c.x * U, z: c.z * U }; };
    const cell = 2 * U;   // (MODULE_CELL people, in the kit's units)
    const add = (o: THREE.Mesh, tris: number) => { this.group.add(o); this.own.push(o); this.fixed.draws++; this.fixed.triangles += tris; };
    // the walls (63.16 (7): none when the compound stands as a structure — the renderer draws that one, damage, rubble and all)
    this.walls = opts.walls === false ? null : buildLayoutLook(plan.walls, p, U);
    if (this.walls) { this.group.add(this.walls.group); this.fixed.draws += this.walls.cost.draws; this.fixed.triangles += this.walls.cost.triangles; }
    // the markings: each slot's outline (white), the lanes' edges (amber), the pad's outline and its ramp's apron (amber)
    const marks: THREE.BufferGeometry[] = [];
    for (const sl of plan.slots) { const c = at(sl.i, sl.j, 3, 1); outline(marks, 3 * cell - 0.4, cell - 0.2, c.x, c.z, '#d9d4c4', 0.12); }
    for (const l of plan.lanes) { const c = at(l.i, l.j, l.cols, l.rows); outline(marks, l.cols * cell - 0.3, l.rows * cell - 0.3, c.x, c.z, '#d6b43a'); }
    { const along = !!plan.pad.alongX, c = along ? at(plan.pad.i, plan.pad.j, 3, 1) : at(plan.pad.i, plan.pad.j, 1, 3); outline(marks, (along ? 3 : 1) * cell - 0.1, (along ? 1 : 3) * cell - 0.1, c.x, c.z, '#d6b43a', 0.3); }   // (69.14: the pad along x)
    for (const b of plan.bays) { const c = at(b.i, b.j, 3, 1); outline(marks, 3 * cell - 0.2, cell + 0.8, c.x, c.z, '#d6b43a', 0.12); }
    const mg = mergeGeometries(marks, false)!;
    marks.forEach(g => g.dispose());
    add(new THREE.Mesh(mg, lambert()), mg.attributes.position.count / 3);
    this.vehicles.name = 'vehicles';
    this.group.add(this.vehicles);
    // the stacker arm parked at its station (its boom down, its spreader low), a set of corner robots in each bay
    const arm = buildHandler('arm', p, livery, { lift: 2.2, reach: 4.6 }), a = at(plan.arm.i, plan.arm.j);
    arm.mesh.position.set(a.x, 0, a.z); arm.mesh.rotation.y = plan.arm.turn * Math.PI / 2;
    add(arm.mesh, arm.triangles);
    this.arm = arm.mesh;
    for (const b of plan.bays) {
      const bots = buildHandler('corner', p, livery, { lift: 0 }), c = at(b.i, b.j, 3, 1);
      bots.mesh.position.set(c.x, 0, c.z); bots.mesh.rotation.y = Math.PI / 2;
      add(bots.mesh, bots.triangles);
    }
    this.group.traverse(o => { if (o !== this.group) { o.matrixAutoUpdate = false; o.updateMatrix(); } });   // (the parts frozen; the group itself placed by its holder)
    this.cost.draws = this.fixed.draws; this.cost.triangles = this.fixed.triangles;
  }

  private frameMesh(variant: FrameVariant): { im: THREE.InstancedMesh; tris: number } {
    let e = this.frames.get(variant);
    if (!e) {
      const f = buildVehicleFrame(this.p, this.livery, { variant, number: this.no++ }), im = new THREE.InstancedMesh(f.mesh.geometry, f.mesh.material, this.capacity);
      im.name = `motorPool:frames:${variant}`;
      im.count = 0; im.matrixAutoUpdate = false;
      this.group.add(im); this.own.push(im);
      e = { im, tris: f.triangles };
      this.frames.set(variant, e);
    }
    return e;
  }
  private carMesh(kind: VehicleKind): { im: THREE.InstancedMesh; tris: number } {
    let e = this.cars.get(kind);
    if (!e) {
      const m = vehicleModel(kind, this.p);
      m.silhouette.visible = false;
      const g = folded(m.root), im = new THREE.InstancedMesh(g, lambert(), this.capacity);
      im.name = `motorPool:vehicles:${kind}`;
      im.count = 0; im.matrixAutoUpdate = false;
      this.vehicles.add(im); this.own.push(im);
      e = { im, tris: g.attributes.position.count / 3 };
      this.cars.set(kind, e);
    }
    return e;
  }

  /** Stand these stacks in the yard (the frames by variant, the vehicles in the open ones by kind): the instances rewritten. */
  place(stacks: readonly MotorPoolStack[]): void {
    const byVariant = new Map<FrameVariant, THREE.Matrix4[]>(), byKind = new Map<VehicleKind, THREE.Matrix4[]>(), deck = new THREE.Matrix4().makeTranslation(0, FRAME_DECK, 0);
    for (const st of stacks) {
      const m = stackPose(this.plan, st), v = variantOf(st.load);
      byVariant.set(v, [...(byVariant.get(v) ?? []), m]);
      if ('vehicle' in st.load) byKind.set(st.load.vehicle, [...(byKind.get(st.load.vehicle) ?? []), m.clone().multiply(deck)]);
    }
    for (const v of byVariant.keys()) this.frameMesh(v);
    for (const k of byKind.keys()) this.carMesh(k);
    let draws = this.fixed.draws, triangles = this.fixed.triangles;
    const write = (e: { im: THREE.InstancedMesh; tris: number }, list: THREE.Matrix4[] | undefined) => {
      const n = list?.length ?? 0;
      list?.forEach((m, k) => e.im.setMatrixAt(k, m));
      e.im.count = n; e.im.visible = n > 0; e.im.instanceMatrix.needsUpdate = true; e.im.computeBoundingSphere();
      if (n) { draws++; triangles += e.tris * n; }
    };
    for (const [v, e] of this.frames) write(e, byVariant.get(v));
    for (const [k, e] of this.cars) write(e, byKind.get(k));
    this.cost.draws = draws; this.cost.triangles = triangles; this.cost.frames = stacks.length;
  }

  dispose(): void {
    // (the walls through their field: the kit's material is shared; the rest is this look's own)
    this.walls?.field.dispose();
    for (const m of this.own) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
  }
}

/** A motor pool's look with these stacks (the sheet's, and play's first). */
export function buildMotorPoolLook(plan: MotorPoolPlan, stacks: readonly MotorPoolStack[], p: UnitPalette, livery: Livery): { group: THREE.Group; vehicles: THREE.Group; cost: MotorPoolLookCost; dispose: () => void; look: MotorPoolLook } {
  const look = new MotorPoolLook(plan, p, livery);
  look.place(stacks);
  return { group: look.group, vehicles: look.vehicles, cost: look.cost, dispose: () => look.dispose(), look };
}

/** A load's key for counting (a vehicle's kind, a container's packing). */
const loadKey = (l: MotorPoolLoad): string => 'vehicle' in l ? `v:${l.vehicle}` : `c:${l.container}`;

/** The spot the next frame takes in a yard holding `stacks` (level by level, as `fillMotorPool`), or null when full. */
export function nextFreeSpot(plan: MotorPoolPlan, stacks: readonly Pick<MotorPoolStack, 'slot' | 'tier'>[]): { slot: number; tier: number } | null {
  const used = new Set(stacks.map(s => `${s.slot}:${s.tier}`)), most = Math.max(0, ...plan.slots.map(s => s.tiers));
  for (let tier = 0; tier < most; tier++) for (let slot = 0; slot < plan.slots.length; slot++)
    if (tier < plan.slots[slot].tiers && !used.has(`${slot}:${tier}`) && (tier === 0 || used.has(`${slot}:${tier - 1}`))) return { slot, tier };
  return null;
}

/**
 * The yard's stacks brought to `loads` without moving what stands (69.10: a frame stacked as it comes, the rest where it
 * was): a load the yard has too few of is set at the next free spot; one it has too many of is taken off the top of a stack
 * of it (nothing left standing on air).
 */
export function restack(plan: MotorPoolPlan, stacks: readonly MotorPoolStack[], loads: readonly MotorPoolLoad[]): MotorPoolStack[] {
  const want = new Map<string, MotorPoolLoad[]>();
  for (const l of loads) want.set(loadKey(l), [...(want.get(loadKey(l)) ?? []), l]);
  let out = [...stacks];
  const have = (k: string) => out.filter(s => loadKey(s.load) === k);
  for (const [k] of new Map([...want, ...out.map(s => [loadKey(s.load), []] as [string, MotorPoolLoad[]])])) {
    const target = want.get(k)?.length ?? 0;
    while (have(k).length > target) {
      const top = have(k).filter(s => !out.some(o => o.slot === s.slot && o.tier === s.tier + 1)).sort((a, b) => b.tier - a.tier)[0] ?? have(k)[0];
      out = out.filter(s => s !== top);
    }
  }
  for (const [k, list] of want) {
    while (have(k).length < list.length) {
      const spot = nextFreeSpot(plan, out);
      if (!spot) return out;
      out = [...out, { ...spot, load: list[0] }];
    }
  }
  return out;
}

interface PoolEntry { team: number; g: THREE.Group; look: MotorPoolLook; stacks: MotorPoolStack[]; /** a compound with no lift (no fine ground): seated on its drawn group once the renderer has made it */ follow?: number }

/**
 * Each side's motor pool in play: built at the first frame it is asked for (its site, its stock), seated on a level yard; its
 * stacks brought to the stock twice a second without moving what stands (`restack`: 69.10's lighters bring frames, a
 * replacement vehicle takes one). Disposed with the host.
 */
export class MotorPools {
  readonly groups: THREE.Group[] = [];
  private readonly entries = new Map<number, PoolEntry>();
  private readonly far: number;
  private since = 0;
  readonly cost: MotorPoolLookCost = { draws: 0, triangles: 0, frames: 0 };

  /**
   * `drawn`: a structure's drawn group (the renderer's) — where a compound has no lift in the sim (no fine ground: core/buildSeat.ts
   * buildBaseLift), the renderer seats it on its plinth and this look is seated on that, so the yard stands inside its walls.
   */
  constructor(private readonly scene: THREE.Scene, state: GameState, private readonly drawn?: (id: number) => THREE.Object3D | undefined) {
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k, scale = person / U;
    const teams = [...new Set(state.players.map(pl => pl.teamIndex))].sort((x, y) => x - y);
    const half = (Math.max(MOTOR_POOL.cols, MOTOR_POOL.rows) / 2) * 2 * person;
    this.far = MOTOR_POOL_VEHICLES_PEOPLE * person;
    for (const t of teams) {
      const site = state.motorPools?.[t] ?? motorPoolSite(state, t), planet = site ? state.planets[site.planetIndex] : undefined;   // (63.1n with 67.75: the pool's own site, its pad's, when the stock is the pool)
      if (!site || !planet) continue;
      const team = TEAMS[t % TEAMS.length], { stacks } = fillMotorPool(MOTOR_POOL, motorPoolLoads(state, t));
      // 63.16 (7): where the compound stands as a structure (core/motorPoolStock.ts withMotorPools, with the stock) the renderer draws its
      // walls and plinth; this look dresses it — frames, markings, handlers — seated at the structure's own frame (one frame, drawn and simulated)
      const own = state.structures.find(s => s.type === 'motorPool' && s.health > 0 && structureTeam(state, s) === t);   // (69.14's fix-forward: with or without a lift — it skipped every pool without fine ground, the yard left empty)
      if (!own) continue;   // 69.14 (the coordinator: "If the pool is drawn, it's simulated"): no compound drawn where none stands in the sim
      const look = new MotorPoolLook(MOTOR_POOL, unitPaletteFor(team), { name: hullName(team.name), mark: 0 }, { walls: !own });
      look.place(stacks);
      const g = new THREE.Group();
      g.name = `motorPool:${t}`;
      g.add(look.group);
      g.scale.setScalar(scale);
      if (own) seatAt(g, planet, { dir: own.surfaceDir, yaw: own.yaw, half, lift: own.baseLift ?? 0 });   // (no lift: until the drawn compound is there to seat on, below)
      else {
        // its gate (−z) toward the front: the yaw about its up that turns −z onto `front` after the upright turn
        const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(site.dir.x, site.dir.y, site.dir.z).normalize());
        const f = new THREE.Vector3(site.front.x, site.front.y, site.front.z).applyQuaternion(q.clone().invert());
        seatOnPlinth(g, planet, { planetIndex: site.planetIndex, dir: site.dir, yaw: Math.atan2(-f.x, -f.z), half, height: FRAME.height * 3 * scale, scale, color: '#8a8578' });
      }
      g.updateMatrixWorld(true);
      scene.add(g);
      this.groups.push(g);
      this.entries.set(t, { team: t, g, look, stacks, ...(own && own.baseLift === undefined ? { follow: own.id } : {}) });
    }
    this.tally();
  }

  private tally(): void {
    this.cost.draws = 0; this.cost.triangles = 0; this.cost.frames = 0;
    for (const e of this.entries.values()) { this.cost.draws += e.look.cost.draws + 1; this.cost.triangles += e.look.cost.triangles; this.cost.frames += e.look.cost.frames; }
  }

  /** A side's pool: its group (the yard's frame: the kit's units, scaled to the match's person), its look, its stacks. */
  entry(team: number): Readonly<PoolEntry> | undefined { return this.entries.get(team); }

  /** The yard's stacks brought to the stock now (`force`: at once; else at most twice a second). */
  refresh(state: GameState, dt: number, force = false): void {
    this.since += dt;
    if (!force && this.since < 0.5) return;
    this.since = 0;
    let changed = false;
    for (const e of this.entries.values()) {
      const next = restack(MOTOR_POOL, e.stacks, motorPoolLoads(state, e.team));
      if (next.length === e.stacks.length && next.every((s, k) => s === e.stacks[k])) continue;
      e.stacks = next; e.look.place(next); changed = true;
    }
    if (changed) this.tally();
  }

  /** Each frame: a pool's vehicles shown only within MOTOR_POOL_VEHICLES_PEOPLE of the camera (one distance a side); with the state, its stacks kept to the stock. */
  update(camera: THREE.Vector3, state?: GameState, dt = 0): void {
    for (const e of this.entries.values()) {
      if (e.follow === undefined) continue;
      const d = this.drawn?.(e.follow);
      if (!d) continue;
      e.g.position.copy(d.position); e.g.quaternion.copy(d.quaternion); e.g.updateMatrixWorld(true);
      e.follow = undefined;
    }
    for (const e of this.entries.values()) e.look.vehicles.visible = e.g.position.distanceTo(camera) < this.far;
    if (state) this.refresh(state, dt);
  }

  dispose(): void {
    for (const e of this.entries.values()) {
      this.scene.remove(e.g);
      const plinth = e.g.getObjectByName('plinth') as THREE.Mesh | undefined;
      if (plinth) { plinth.geometry.dispose(); (plinth.material as THREE.Material).dispose(); }
      e.look.dispose();
    }
    this.entries.clear(); this.groups.length = 0;
  }
}
